import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useMemo, useState } from "react";
import DeleteIcon from "../../components/icons/DeleteIcon";
import { colors } from "../../config/theme";
import PromptModal from "../../components/modals/PromptModal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDebounce } from "usehooks-ts";

import Pagination from "../../components/Pagination";
import { apiConfig } from "../../config/apiConfig";

import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";

import { format } from "date-fns";

import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import apiService from "../../service/apiService";
import CreatePartnerAccountModal from "./CreatePartnerAccountModal";

const sourceLabel = (source?: PartnerRequest["source"]) => {
  if (source === "partners") return "Partners";
  if (source === "web") return "Venez web";
  return "—";
};

const TableContent = ({ Request }: { Request: PartnerRequest }) => {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const isPartnersSource = Request.source === "partners";
  const isWebSource = Request.source === "web";
  const accountCreated = Boolean(
    Request.accountCreated || Request.convertedUserId,
  );

  const deletePartnerRequest = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`partners/requests/${id}`, token);
  });

  const deletePequest = (id: string) => {
    deletePartnerRequest.mutate(id, {
      onError() {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        queryClient.invalidateQueries("getPartnersRequest");
        toastSuccess(general.fr.message.partnerDeleted);
        setShowModal(false);
      },
    });
  };

  return (
    <>
      <CTableRow key={Request?.id}>
        <CTableDataCell className="text-sm">
          {format(new Date(Request.createdAt), "dd/MM/yyyy")}
        </CTableDataCell>
        <CTableDataCell className="text-sm">{Request?.fullName}</CTableDataCell>
        <CTableDataCell className="text-sm">{Request?.email}</CTableDataCell>
        <CTableDataCell className="text-sm">{Request?.phone}</CTableDataCell>
        <CTableDataCell className="text-sm">
          {Request?.businessName}
        </CTableDataCell>
        <CTableDataCell className="text-sm">
          {Request?.city?.label}
        </CTableDataCell>
        <CTableDataCell className="text-sm">
          {sourceLabel(Request.source)}
        </CTableDataCell>

        <CTableDataCell>
          <div className="d-flex align-items-center gap-2">
            {isPartnersSource && (
              <button
                type="button"
                className="btn btn-sm btn-primary text-white"
                disabled={accountCreated}
                onClick={() => setShowCreateAccount(true)}
              >
                {accountCreated ? "Compte créé" : "Créer le compte"}
              </button>
            )}
            {isWebSource && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  window.open("/places/new", "_blank", "noopener,noreferrer")
                }
              >
                Créer l&apos;établissement
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="outline-none border-0 bg-transparent"
            >
              <DeleteIcon
                width="20"
                height="20"
                fill={colors.primary}
                className="ms-2"
              />
            </button>
          </div>
        </CTableDataCell>
      </CTableRow>
      <PromptModal
        onAccept={() => deletePequest(Request?.id)}
        onClose={() => setShowModal(false)}
        open={showModal}
      />
      <CreatePartnerAccountModal
        request={Request}
        open={showCreateAccount}
        onClose={() => setShowCreateAccount(false)}
      />
    </>
  );
};

const PAGE_SIZE = 10;
export default function PartnerRequests() {
  const { token } = useCookie("vToken");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const search = useDebounce(searchQuery, 2000);

  const filter = useMemo(
    () => ({
      page: currentPage,
      limit: PAGE_SIZE,
      query: search,
    }),
    [currentPage, search],
  );

  const { data: partnerRequests } = useQuery(
    ["getPartnersRequest", filter],
    () => {
      const endpoint = apiConfig.partnair.request(filter);
      return apiService.MakeGetRequest(endpoint, token);
    },
  );

  return (
    <div>
      <div className="d-flex  justify-content-between" id="partners">
        <div className="d-flex align-items-center gap-2 p-3"></div>
        <div className="d-flex align-items-center justify-content-between mt-2 mb-3">
          <div className="search-form">
            <div className="input-field">
              <MagnifyingGlassIcon color="gray" width={20} />
              <input
                type="text"
                className="w-50 border-0  outline-none rounded-sm p-1 text-gray fs-6"
                placeholder="Rechercher"
                onChange={(e) =>
                  setSearchQuery(e?.target?.value?.toLowerCase())
                }
              />
            </div>
          </div>
        </div>
      </div>
      <CTable>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" className="text-sm">
              Date
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              Nom complet
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              Email
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              Téléphone
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              Nom de l'entreprise
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              Ville
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              Source
            </CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {partnerRequests?.items?.map((Request: PartnerRequest) => (
            <TableContent key={Request?.id} Request={Request} />
          ))}
        </CTableBody>
      </CTable>
      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        siblingCount={0}
        totalCount={partnerRequests?.meta?.totalPages}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
}
